import { create } from 'zustand';
import { useThemeStore } from './themeStore';

// =============================================================================
// SEMANTIC REFERENCE — DO NOT DELETE
// =============================================================================
// Event type codes (backend value of traffic_event.event_type):
//   EVT_TP_STP = 정차          (Stop / Parking)
//   EVT_TP_ILP = 불법주정차    (Illegal Parking)
//   EVT_TP_SPD = 속도위반      (Speeding)
//   EVT_TP_JW  = 무단횡단      (Jaywalking)
//   EVT_TP_WWD = 역주행        (Wrong Way Driving)
//   EVT_TP_EXC = 대기행렬초과  (Queue Exceeded)
//   EVT_TP_SLV = 정지선위반    (Stop Line Violation)
//   EVT_TP_PED = 보행자        (Pedestrian)
//
// Network status codes (common code 201):
//   201002 = online  / 정상
//   201001 = offline / 이상
// =============================================================================

// -----------------------------------------------------------------------------
// palette.category — 10-color categorical palette (theme-invariant).
// Material-based palette validated OK on both light and dark chart backgrounds.
// Used by statistic chart series, event-type series, and any multi-series chart
// that needs distinct-looking colors.
// -----------------------------------------------------------------------------
const CATEGORY_PALETTE = [
  '#FF5959', //  1  연빨강          light red       — EVT_TP_STP (정차)
  '#BA68C8', //  2  연보라          light purple    — EVT_TP_ILP (불법주정차)
  '#FF9800', //  3  주황            orange          — EVT_TP_SPD (속도위반)
  '#42A5F5', //  4  연파랑          light blue      — EVT_TP_JW  (무단횡단)
  '#66BB6A', //  5  연녹색          light green     — EVT_TP_WWD (역주행)
  '#9DA5FF', //  6  연보라파랑      periwinkle      — EVT_TP_EXC (대기행렬초과)
  '#9C27B0', //  7  진보라          deep purple     — EVT_TP_SLV (정지선위반)
  '#7B4019', //  8  갈색            brown           — EVT_TP_PED (보행자)
  '#26C6DA', //  9  청록            cyan            — new
  '#EC407A', // 10  핑크            pink            — new
] as const;

// -----------------------------------------------------------------------------
// eventTypeColor — semantic mapping (event_type code → color).
// Derived from CATEGORY_PALETTE slots 0–7 to avoid duplicate hex literals.
// -----------------------------------------------------------------------------
export type EventTypeCode =
  | 'EVT_TP_STP'
  | 'EVT_TP_ILP'
  | 'EVT_TP_SPD'
  | 'EVT_TP_JW'
  | 'EVT_TP_WWD'
  | 'EVT_TP_EXC'
  | 'EVT_TP_SLV'
  | 'EVT_TP_PED';

const EVENT_TYPE_COLOR: Record<EventTypeCode, string> = {
  EVT_TP_STP: CATEGORY_PALETTE[0], // 정차
  EVT_TP_ILP: CATEGORY_PALETTE[1], // 불법주정차
  EVT_TP_SPD: CATEGORY_PALETTE[2], // 속도위반
  EVT_TP_JW: CATEGORY_PALETTE[3], // 무단횡단
  EVT_TP_WWD: CATEGORY_PALETTE[4], // 역주행
  EVT_TP_EXC: CATEGORY_PALETTE[5], // 대기행렬초과
  EVT_TP_SLV: CATEGORY_PALETTE[6], // 정지선위반
  EVT_TP_PED: CATEGORY_PALETTE[7], // 보행자
};

// -----------------------------------------------------------------------------
// palette.mapLane — digital twin map polygon colors (theme-invariant pastels).
// Retained per design decision (keep map overlay colors distinct from chart).
// -----------------------------------------------------------------------------
const MAP_LANE_PALETTE = [
  '#FFCF73', // 살구            peach
  '#FF3CEC', // 핫핑크          hot pink
  '#87FF73', // 연두            light green
  '#FF7373', // 연산호          coral
  '#7385FF', // 라벤더          lavender
  '#65c9f7', // 하늘            sky blue
] as const;

// -----------------------------------------------------------------------------
// palette.road (theme-aware) — access road series palette, up to 8 roads.
// Dark mode: brighter tones; light mode: darker tones for white-background contrast.
// -----------------------------------------------------------------------------
const ROAD_PALETTE_DARK = [
  '#D60000', // 빨강            red
  '#8E24AA', // 보라            purple
  '#FF6F00', // 주황            orange
  '#009439', // 녹색            green
  '#1E88E5', // 파랑            blue
  '#00ACC1', // 청록            teal
  '#FDD835', // 노랑            yellow
  '#EC407A', // 핑크            pink
] as const;

const ROAD_PALETTE_LIGHT = [
  '#C62828', // 짙은 빨강       dark red
  '#6A1B9A', // 짙은 보라       dark purple
  '#E65100', // 짙은 주황       dark orange
  '#2E7D32', // 짙은 녹색       dark green
  '#1565C0', // 짙은 파랑       dark blue
  '#00838F', // 짙은 청록       dark teal
  '#F9A825', // 짙은 노랑       dark yellow
  '#AD1457', // 짙은 핑크       dark pink
] as const;

export const NEWCOLOR_PALETTE = {
  danger: '#FF3B30',
  warning: '#FC6605',
  greenPrimary: '#00C483',
  bluePrimary: '#007AFF',
  yellowPrimary: '#E0B300',
  greyPrimary: '#666666',
  accentGreen: '#2B9756',
  accentBlue: '#007AFF',
  accentYellow: '#FFCC00',
  accentGrape: '#8D66FD',
  accentCyan: '#4BF1E6',
  accentOrange: '#FC6605',
  accentSoftGreen: '#87CEEB',
  accentSoftRed: '#EE2E6B',
  accentSoftYellow: '#FFF7A4',
  accentSoftGrape: '#AE00FF',
  accentDeepBlue: '#135A78',
} as const;

// =============================================================================
// Store shape
// =============================================================================

interface ChartColors {
  axisLabel: string;
  axisLine: string;
  splitLine: string;
  legendText: string;
}

interface ApexChartColors {
  foreColor: string;
  gridBorderColor: string;
  /** Neutral color for the "전체"(aggregate) line in multi-series ApexCharts. */
  seriesTotal: string;
}

interface StatusColors {
  /** network_status 201002 — online / 정상 */
  online: string;
  /** network_status 201001 — offline / 이상 */
  offline: string;
  /** intermediate / caution state (yellow) */
  warning: string;
  /** neutral state — "전체" bucket, undefined status, etc. */
  neutral: string;
}

interface Palettes {
  /** Access road series palette (theme-aware), 8 colors for up to 8 roads. */
  road: readonly string[];
  /** Generic categorical palette (theme-invariant), 10 colors. */
  category: readonly string[];
  /** Digital twin map polygon palette (theme-invariant), 6 pastels. */
  mapLane: readonly string[];
}

interface ColorState {
  NEWCOLOR: Record<string, string>;
  chart: ChartColors;
  apexChart: ApexChartColors;
  palette: Palettes;
  eventTypeColor: Record<EventTypeCode, string>;
  status: StatusColors;
  eventBorderInactive: string;
}

// =============================================================================
// Theme-specific values
// =============================================================================

const lightColors: ColorState = {
  NEWCOLOR: NEWCOLOR_PALETTE,
  chart: {
    axisLabel: '#475569',
    axisLine: '#cbd5e1',
    splitLine: '#e2e8f0',
    legendText: '#334155',
  },
  apexChart: {
    foreColor: '#545454',
    gridBorderColor: '#e6e6e6',
    seriesTotal: '#333333',
  },
  palette: {
    road: ROAD_PALETTE_LIGHT,
    category: CATEGORY_PALETTE,
    mapLane: MAP_LANE_PALETTE,
  },
  eventTypeColor: EVENT_TYPE_COLOR,
  status: {
    online: '#15803d', // 짙은 녹색        dark green
    offline: '#dc2626', // 짙은 빨강        dark red
    warning: '#EE9F17', // 짙은 주황-노랑   dark amber
    neutral: '#6b7280', // 중성 회색        neutral gray
  },
  eventBorderInactive: 'var(--db-border)',
};

const darkColors: ColorState = {
  NEWCOLOR: NEWCOLOR_PALETTE,
  chart: {
    axisLabel: '#CBD5E1',
    axisLine: '#334155',
    splitLine: '#334155',
    legendText: '#CBD5E1',
  },
  apexChart: {
    foreColor: '#c0c8d0',
    gridBorderColor: '#2b465f',
    seriesTotal: '#8899aa',
  },
  palette: {
    road: ROAD_PALETTE_DARK,
    category: CATEGORY_PALETTE,
    mapLane: MAP_LANE_PALETTE,
  },
  eventTypeColor: EVENT_TYPE_COLOR,
  status: {
    online: '#009439', // 녹색              green
    offline: '#EA494E', // 부드러운 빨강     soft red
    warning: '#FDCA6A', // 노랑              yellow
    neutral: '#6b7280', // 중성 회색         neutral gray
  },
  eventBorderInactive: '#2B465F',
};

function getColors(isDark: boolean): ColorState {
  return isDark ? darkColors : lightColors;
}

export const useColorStore = create<ColorState>(() => getColors(useThemeStore.getState().isDark));

// Auto-sync with theme changes
useThemeStore.subscribe((state) => {
  useColorStore.setState(getColors(state.isDark));
});
